import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, collection, addDoc, query, where, onSnapshot, setDoc } from 'firebase/firestore';
import { 
  Upload, 
  Clock, 
  CheckCircle2, 
  Plus,
  Wallet,
  History,
  Users,
  Camera,
  LogOut, 
  ArrowUpRight, 
  ChevronRight, 
  Copy,
  Smartphone,
  ShieldCheck,
  User,
  UserCircle,
  CreditCard,
  MapPin,
  Save,
  Loader2,
  FileText
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/StatusBadge';
import BottomNav from '../components/BottomNav';
import { processNIC } from '../utils/nicUtils';
import { sendSMSWithRateLimit } from '../utils/smsService';
import SignaturePad from '../components/SignaturePad';
import {
  buildLoanEligibilityAssessment,
  enrichWithAdjustedSuggestion,
} from '../utils/loanRisk';
import { sealSensitiveDoc, unsealSensitiveDoc } from '../utils/security';

import { validateSLPhone, validateSLNIC, sanitizePhone, sanitizeNIC } from '../utils/validation';

export default function UserDashboard() {
  const navigate = useNavigate();
  const { currentUser, userData, logout } = useAuth();
  const [config, setConfig] = useState(null);
  const [loans, setLoans] = useState([]);
  const [repayments, setRepayments] = useState([]);
  const [showApply, setShowApply] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showRepay, setShowRepay] = useState(false);
  const [repayOption, setRepayOption] = useState('installment');
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(2);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [signature, setSignature] = useState(null);
  const [userOtp, setUserOtp] = useState('');
  const [sentUserOtp, setSentUserOtp] = useState('');
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [isVerifyingPhone, setIsVerifyingPhone] = useState(false);

  const [formData, setFormData] = useState({
    personal: {
      fullName: '',
      nicNumber: '',
      dob: '',
      gender: 'Male',
      maritalStatus: 'Single',
      mobileNumber: '',
      whatsappNumber: '',
      currentAddress: '',
      permanentAddress: '',
    },
    employment: {
      type: 'Employed',
      jobTitle: '',
      workLocation: '',
      monthlyIncome: '',
      dailyIncome: '',
      years: '',
    },
    financial: {
      expenses: '',
      hasLoans: 'No',
      loanDetails: '',
      dependents: '',
      monthlyDebt: '',
    },
    loan: {
      interestRate: '10',
      rateType: 'monthly',
      amount: '',
      duration: '',
      durationUnit: 'months',
      purpose: '',
      bankName: '',
      branch: '',
      accountNumber: '',
      accountHolder: '',
      paymentMethod: 'monthly',
    },
    guarantors: [
      { name: '', nic: '', phone: '' }
    ]
  });

  const [personalFiles, setPersonalFiles] = useState({
    nicFront: null,
    nicBack: null,
    selfie: null,
    statements: null
  });

  useEffect(() => {
    getDoc(doc(db, 'configs', 'main')).then(snap => {
      if (snap.exists()) {
        const data = snap.data();
        setConfig(data);
        if (data.interestRate) {
          setFormData(prev => ({
            ...prev,
            loan: { ...prev.loan, interestRate: data.interestRate.toString() }
          }));
        }
      }
    });

    const qLoans = query(collection(db, 'loans'), where('userId', '==', currentUser.uid));
    const unsubscribeLoans = onSnapshot(qLoans, (snapshot) => {
      setLoans(snapshot.docs.map(doc => unsealSensitiveDoc({ id: doc.id, ...doc.data(), type: 'loan' })));
    }, (error) => {
      console.error('Loan Snapshot failed:', error);
      logout();
    });

    const qRepayments = query(collection(db, 'repayments'), where('userId', '==', currentUser.uid));
    const unsubscribeRepayments = onSnapshot(qRepayments, (snapshot) => {
      setRepayments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'repayment' })));
    }, (error) => {
      console.error('Repayment Snapshot failed:', error);
      logout();
    });

    if (userData?.personal && !formData.personal.fullName) {
      setFormData(prev => ({
        ...prev,
        personal: { ...prev.personal, ...userData.personal }
      }));
    }

    return () => {
      unsubscribeLoans();
      unsubscribeRepayments();
    };
  }, [currentUser, userData]);

  const maxAllowed = userData?.creditLimit || config?.startingLoanAmount || 15000;
  const hasOngoingLoan = loans.some(loan => ['pending_guarantor', 'pending_admin', 'approved'].includes(loan.status));

  const calculateInterest = (amount, loanData) => {
    if (!amount || !loanData.interestRate) return 0;
    const rate = Number(loanData.interestRate);
    const duration = Number(loanData.duration || 0);

    if (loanData.rateType === 'annual') return (Number(amount) * (rate / 100)) * (duration / 12);
    if (loanData.rateType === 'monthly') return (Number(amount) * (rate / 100)) * duration;
    return 0;
  };

  const calculateServiceFee = (amount) => {
    if (!amount || !config?.serviceFee) return 0;
    return Number(amount) * (config.serviceFee / 100);
  };

  const handleNICChange = (e) => {
    const val = sanitizeNIC(e.target.value);
    // Note: updateFormData is assumed to be defined elsewhere in this file
    if (typeof updateFormData === 'function') {
       updateFormData('personal', 'nicNumber', val);
    }
    
    if (validateSLNIC(val)) {
      const res = processNIC(val);
      if (!res.error) {
        setFormData(prev => ({
          ...prev,
          personal: {
            ...prev.personal,
            nicNumber: val,
            dob: res.date_of_birth,
            gender: res.gender
          }
        }));
      }
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();

    const hasPersonalData = Boolean(userData?.personal?.nicNumber && userData?.personal?.nicFrontUrl);

    if (currentStep < 7) {
      if (currentStep === 2) {
        if (Number(formData.loan.amount) > maxAllowed) return alert('Amount exceeds limit');
        if (config?.maxDuration && Number(formData.loan.duration) > config.maxDuration) {
          return alert(`Tenure exceeds the system limit of ${config.maxDuration} months`);
        }
        if (hasPersonalData) {
          setCurrentStep(5);
          return;
        }
      }
      if (currentStep === 4) {
        if (!validateSLNIC(formData.personal.nicNumber)) return alert('Please enter a valid Sri Lankan NIC number');
        if (!validateSLPhone(formData.personal.mobileNumber)) return alert('Please enter a valid Sri Lankan mobile number');
        if (!isPhoneVerified) return alert('Please verify your mobile number via OTP first');
      }
      if (currentStep === 6) {
        for (const g of formData.guarantors) {
          if (!validateSLNIC(g.nic)) return alert(`Please enter a valid NIC for guarantor ${g.name || ''}`);
          if (!validateSLPhone(g.phone)) return alert(`Please enter a valid mobile number for guarantor ${g.name || ''}`);
        }
      }
      if (currentStep === 3) {
        const hasPersistentIdentity = userData?.personal?.nicFrontUrl && userData?.personal?.nicBackUrl && userData?.personal?.selfieUrl;
        if (!hasPersistentIdentity && (!personalFiles.nicFront || !personalFiles.nicBack || !personalFiles.selfie)) {
          return alert('Please upload all required identity documents (NIC Front, NIC Back, Selfie)');
        }
      }
      if (currentStep === 5) {
        if (!personalFiles.statements || personalFiles.statements.length === 0) {
          return alert('Please upload your last 3-month bank statements');
        }
      }
      setCurrentStep(prev => Math.min(prev + 1, 7));
      return;
    }

    try {
      setLoading(true);
      
      const uploadFile = async (file, path) => {
        const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'YOUR_CLOUD_NAME';
        const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'YOUR_UPLOAD_PRESET';
        
        const formData = new FormData();
        if (typeof file === 'string') {
          const blob = await (await fetch(file)).blob();
          formData.append('file', blob);
        } else {
          formData.append('file', file);
        }
        formData.append('upload_preset', uploadPreset);
        
        // Cloudinary auto detects image or raw file (PDF) types
        const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`, {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error?.message || 'Cloudinary upload failed');
        }
        return data.secure_url;
      };

      let nicFrontUrl = userData?.personal?.nicFrontUrl;
      let nicBackUrl = userData?.personal?.nicBackUrl;
      let selfieUrl = userData?.personal?.selfieUrl;

      if (!nicFrontUrl && personalFiles.nicFront) nicFrontUrl = await uploadFile(personalFiles.nicFront, 'nic_front');
      if (!nicBackUrl && personalFiles.nicBack) nicBackUrl = await uploadFile(personalFiles.nicBack, 'nic_back');
      if (!selfieUrl && personalFiles.selfie) selfieUrl = await uploadFile(personalFiles.selfie, 'selfie');

      let borrowerSignatureUrl = null;
      if (signature) {
        borrowerSignatureUrl = await uploadFile(signature, 'borrower_signature');
      }

      const guarantorsProcessed = formData.guarantors.map((g, i) => ({
        ...g,
        status: 'pending'
      }));

      let statementsUrls = [];
      if (personalFiles.statements && personalFiles.statements.length > 0) {
        for (let i = 0; i < personalFiles.statements.length; i++) {
          const url = await uploadFile(personalFiles.statements[i], `statements_3mo_${i}`);
          statementsUrls.push(url);
        }
      }

      const totalInterest = calculateInterest(formData.loan.amount, formData.loan);
      const totalServiceFee = calculateServiceFee(formData.loan.amount);
      const principal = Number(formData.loan.amount);
      const totalPayable = principal + totalInterest + totalServiceFee;
      const durationMonths = Math.max(1, Math.floor(Number(formData.loan.duration) || 1));
      const monthlyIncome = Number(formData.employment.monthlyIncome) || 0;
      const existingMonthlyDebt = Math.max(0, Number(formData.financial.monthlyDebt) || 0);

      const totalPayableForPrincipal = (p) => {
        const amt = Number(p) || 0;
        const ti = calculateInterest(amt, formData.loan);
        const tf = calculateServiceFee(amt);
        return amt + ti + tf;
      };

      let eligibility = buildLoanEligibilityAssessment({
        monthlyIncome,
        existingMonthlyDebt,
        requestedLoan: principal,
        durationMonths,
        totalPayable,
      });
      eligibility = enrichWithAdjustedSuggestion(eligibility, totalPayableForPrincipal);

      const customLoanId = `LN-${Math.random().toString(36).substring(2, 5).toUpperCase()}${Date.now().toString(36).slice(-3).toUpperCase()}`;
      
      const loanData = {
        loanId: customLoanId,
        userId: currentUser.uid,
        userName: userData.displayName,
        company: userData.company,
        amountRequested: principal,
        interestRate: Number(formData.loan.interestRate),
        rateType: formData.loan.rateType,
        serviceFee: config?.serviceFee || 0,
        totalPayable,
        status: 'pending_guarantor',
        borrowerSignatureUrl,
        createdAt: new Date().toISOString(),
        eligibility,
        guarantor: {
          name: formData.guarantors[0].name,
          phone: formData.guarantors[0].phone,
          status: 'pending'
        },
        details: {
          ...formData,
          personal: { ...formData.personal, nicFrontUrl, nicBackUrl, selfieUrl },
          employment: { ...formData.employment, statementsUrl: statementsUrls.join(',') },
          guarantors: guarantorsProcessed
        }
      };

      await setDoc(doc(db, 'users', currentUser.uid), sealSensitiveDoc({
        ...userData,
        personal: { ...formData.personal, nicFrontUrl, nicBackUrl, selfieUrl },
        riskyUser: Boolean(eligibility.flaggedRiskyUser),
        lastEligibilityDecision: eligibility.decision,
        lastEligibilityRiskScore: eligibility.riskScore,
        updatedAt: new Date().toISOString()
      }), { merge: true });

      const loanRef = await addDoc(collection(db, 'loans'), sealSensitiveDoc(loanData));

      const apikey = config?.smsApiKey || process.env.REACT_APP_SMS_API_KEY;
      const apitoken = config?.smsApiToken || process.env.REACT_APP_SMS_API_TOKEN;

      if (apikey && apitoken) {
        formData.guarantors.forEach(guarantor => {
          if (guarantor.phone) {
             const link = `${window.location.origin}/guarantor-verify/${loanRef.id}`;
             const text = `Finova: You've been named as a guarantor for ${userData.displayName || userData.email}'s loan. Please verify here: ${link}`;
             sendSMSWithRateLimit(guarantor.phone, text, {
                apikey: config.smsApiKey || process.env.REACT_APP_SMS_API_KEY,
                apitoken: config.smsApiToken || process.env.REACT_APP_SMS_API_TOKEN,
                from: config.smsFrom || process.env.REACT_APP_SMS_SENDER_ID
             }).then(res => {
                if (res.status === 'error') {
                   console.error('SMS Rate Limit or Error:', res.message);
                }
             }).catch(e => console.error('SMS Notification Failed:', e));
          }
        });
      }
      
      setShowApply(false);
      setCurrentStep(1);
      alert('Your application has been submitted successfully! Guarantors will be notified.');
    } catch (err) {
      console.error(err);
      alert('Failed to submit application: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRepaySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const activeLoan = loans.find(l => l.status === 'approved');
      if (!activeLoan) return;

      const installments = activeLoan.installments || [];
      const nextInstallment = installments.find(ins => ins.status === 'unpaid');
      
      if (!nextInstallment && repayOption === 'installment') {
        return alert('All installments are already paid or pending verification.');
      }

      let penalty = 0;
      if (nextInstallment && new Date() > new Date(nextInstallment.dueDate)) {
        penalty = Math.round(nextInstallment.amount * 0.05);
      }

      const amountToPay = repayOption === 'installment' 
        ? (nextInstallment.amount + penalty) 
        : activeLoan.remainingBalance;

      await addDoc(collection(db, 'repayments'), {
        userId: currentUser.uid,
        userName: userData.displayName,
        loanId: activeLoan.id,
        amount: amountToPay,
        penalty: penalty,
        option: repayOption,
        status: 'pending_admin',
        createdAt: new Date().toISOString()
      });

      setShowRepay(false);
      alert('Repayment request submitted. Admin will verify your payment and penalty if applicable.');
    } catch (error) {
      console.error(error);
      alert('Failed to submit repayment request.');
    } finally {
      setLoading(false);
    }
  };

  const hasPersonalData = Boolean(userData?.personal?.nicNumber && userData?.personal?.nicFrontUrl);

  const prevStep = () => setCurrentStep(prev => {
    if (prev === 5 && hasPersonalData) return 2;
    return Math.max(prev - 1, 1);
  });

  const handleSendUserOTP = async () => {
    const phone = formData.personal.mobileNumber;
    const apikey = config?.smsApiKey || process.env.REACT_APP_SMS_API_KEY;
    const apitoken = config?.smsApiToken || process.env.REACT_APP_SMS_API_TOKEN;

    if (!validateSLPhone(phone)) return alert('Please enter a valid mobile number.');
    if (!apikey || !apitoken) return alert('SMS service not configured.');

    setIsVerifyingPhone(true);
    try {
      const generated = Math.floor(100000 + Math.random() * 900000).toString();
      const text = `Finova: Your OTP for phone verification is ${generated}. Limit: 2 per hour.`;
      
      const res = await sendSMSWithRateLimit(phone, text, {
        apikey: config.smsApiKey,
        apitoken: config.smsApiToken,
        from: config.smsFrom
      });

      if (res.status === 'queued' || res.status === 'success') {
        setSentUserOtp(generated);
        setShowOtpInput(true);
        alert('OTP sent successfully!');
      } else {
        alert(res.message || 'Failed to send OTP');
      }
    } catch (err) {
      console.error(err);
      alert('SMS service error');
    } finally {
      setIsVerifyingPhone(false);
    }
  };

  const handleVerifyUserOTP = () => {
    if (userOtp === sentUserOtp && sentUserOtp !== '') {
      setIsPhoneVerified(true);
      setShowOtpInput(false);
      alert('Phone number verified successfully!');
    } else {
      alert('Invalid OTP. Please try again.');
    }
  };

  const updateFormData = (section, field, value) => {
    if (section === 'personal' && field === 'mobileNumber') {
      setIsPhoneVerified(false);
      setShowOtpInput(false);
      setSentUserOtp('');
    }
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  return (
    <div className="min-h-screen bg-stripe-bg pb-24">
      {/* Header */}
      <header className="page-header">
        <div className="page-header-content max-w-5xl">
          <div className="flex items-center gap-3">
            <img src="/assets/images/finovalogoor.png" alt="Finova" className="h-7 w-auto object-contain" />
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/profile')} 
              className="text-right hidden sm:block group"
            >
              <p className="text-sm font-black text-stripe-dark leading-none group-hover:text-primary-600 transition-colors uppercase tracking-tight">{userData?.displayName || 'User profile'}</p>
            </button>

            <button onClick={logout} className="p-2 text-stripe-slate hover:text-stripe-dark transition-colors" title="Sign out">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-stripe-dark tracking-tight">Dashboard</h2>
          <p className="text-sm text-stripe-slate mt-0.5">Manage your credit line and applications.</p>
        </div>

        {/* Credit Summary */}
        <div className="s-card mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-stripe-slate uppercase tracking-wider mb-1">Available Limit</p>
            <p className="text-3xl font-bold text-stripe-dark">LKR {Math.round(maxAllowed || 0).toLocaleString()}</p>
            <p className="text-sm text-stripe-slate mt-1">{config?.interestRate || formData.loan.interestRate}% {formData.loan.rateType} interest</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`badge ${hasOngoingLoan ? 'badge-success' : 'badge-neutral'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${hasOngoingLoan ? 'bg-green-500' : 'bg-stripe-slate'}`} />
              {hasOngoingLoan ? 'Active facility' : 'No active loan'}
            </span>
          </div>
        </div>

        {!showApply && !showRepay && (
          <div className="space-y-6">
            {/* Active Loan */}
            {hasOngoingLoan ? (
              loans.filter(l => l.status === 'approved').map(loan => (
                <div key={loan.id} className="s-card space-y-6">
                   <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                         <h3 className="text-lg font-bold text-stripe-dark tracking-tight">Active Loan Facility</h3>
                         <p className="text-xs text-stripe-slate mt-1 font-mono uppercase tracking-widest opacity-70">Reference: {loan.loanId || loan.id.substring(0, 8)}</p>
                      </div>
                      <div className="flex items-center gap-4">
                         <div className="text-right hidden md:block">
                            <p className="text-xs font-semibold text-stripe-slate uppercase tracking-wider">Remaining Balance</p>
                             <p className="text-xl font-bold text-stripe-dark">LKR {Math.round(loan.remainingBalance || 0).toLocaleString()}</p>
                         </div>
                         <button 
                            onClick={() => setShowRepay(true)}
                            className="btn-primary"
                          >
                            Make Payment
                          </button>
                      </div>
                   </div>

                   <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-stripe-border">
                      <div>
                         <p className="text-xs font-semibold text-stripe-slate uppercase tracking-wider">Monthly Installment</p>
                          <p className="text-base font-medium text-stripe-dark mt-1">LKR {Math.round(loan.installments?.[0]?.amount || 0).toLocaleString()}</p>
                      </div>
                      <div>
                         <p className="text-xs font-semibold text-stripe-slate uppercase tracking-wider">Next Due Date</p>
                         <p className="text-base font-medium text-stripe-dark mt-1">{loan.installments?.find(i => i.status === 'unpaid')?.dueDate ? new Date(loan.installments?.find(i => i.status === 'unpaid')?.dueDate).toLocaleDateString() : 'COMPLETE'}</p>
                      </div>
                      <div>
                         <p className="text-xs font-semibold text-stripe-slate uppercase tracking-wider">Total Payable</p>
                          <p className="text-base font-medium text-stripe-dark mt-1">LKR {Math.round(loan.totalPayable || 0).toLocaleString()}</p>
                      </div>
                   </div>
                </div>
              ))
            ) : (
              /* Empty State - Only show if NO active OR pending loans */
              <div className="s-card py-12 flex flex-col items-center justify-center text-center">
                 <div className="w-16 h-16 bg-stripe-bg rounded-full flex items-center justify-center mb-4 border border-stripe-border">
                    <Wallet size={24} className="text-stripe-slate" />
                 </div>
                 <h3 className="text-lg font-bold text-stripe-dark mb-2">No Active Loans</h3>
                 <p className="text-sm text-stripe-slate max-w-sm mb-6">Apply for a new loan to get started with your credit facility.</p>
                 <button 
                    onClick={() => setShowApply(true)}
                    className="btn-primary"
                  >
                    <Plus size={16} /> New Application
                  </button>
              </div>
            )}

            {/* If there's a pending application but no approved loan, make sure we show a status banner */}
            {loans.some(l => ['pending_guarantor', 'pending_admin'].includes(l.status)) && !loans.some(l => l.status === 'approved') && (
               <div className="s-card bg-primary-50 border-primary-100 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                     <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-primary-600 shadow-sm">
                        <Clock size={20} />
                     </div>
                     <div className="text-left">
                        <p className="text-sm font-bold text-stripe-dark">Application Processing</p>
                        <p className="text-xs text-stripe-slate">Your request is currently being verified by our team.</p>
                     </div>
                  </div>
                  <button 
                    onClick={() => {
                        const pendingLoan = loans.find(l => ['pending_guarantor', 'pending_admin'].includes(l.status));
                        if (pendingLoan) navigate(`/application-progress/${pendingLoan.id}`);
                    }} 
                    className="text-[10px] font-bold text-primary-600 uppercase tracking-widest hover:underline"
                  >
                    View Progress
                  </button>
               </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
               {/* Pending Queue */}
               <div className="s-card">
                  <div className="flex justify-between items-center mb-4">
                     <h4 className="font-semibold text-stripe-dark flex items-center gap-2">
                        <Clock size={16} /> Pending Applications
                     </h4>
                  </div>
                  <div className="space-y-3">
                     {loans.filter(l => l.status !== 'approved').length > 0 ? (
                        loans.filter(l => l.status !== 'approved').map(l => (
                           <div key={l.id} className="p-4 bg-stripe-bg rounded-xl border border-stripe-border flex items-center justify-between">
                              <div>
                                 <p className="text-sm font-semibold text-stripe-dark">{l.loanId || l.id.substring(0, 8)} · LKR {Math.round(l.amountRequested).toLocaleString()}</p>
                                 <p className="text-xs text-stripe-slate">{new Date(l.createdAt).toLocaleDateString()}</p>
                              </div>
                               <div className="flex items-center gap-2">
                                  {l.status === 'pending_guarantor' && (
                                     <button 
                                       onClick={() => {
                                          const link = `${window.location.origin}/guarantor-verify/${l.id}`;
                                          navigator.clipboard.writeText(link);
                                          alert('Guarantor verification link copied! Share this with your guarantor.');
                                       }}
                                       className="p-2 text-stripe-blue hover:bg-stripe-blue/10 rounded-lg transition-all"
                                       title="Copy link for guarantor"
                                     >
                                        <Copy size={14} />
                                     </button>
                                  )}
                                  <StatusBadge status={l.status} />
                                  <button 
                                     onClick={() => navigate(`/application-progress/${l.id}`)}
                                     className="p-2 text-stripe-slate hover:text-stripe-dark transition-colors"
                                     title="Track Progress"
                                  >
                                     <ChevronRight size={14} />
                                  </button>
                               </div>
                           </div>
                        ))
                     ) : (
                        <div className="py-8 text-center text-sm text-stripe-slate">No pending applications</div>
                     )}
                  </div>
               </div>

               {/* History / Transaction Feed */}
               <div className="s-card overflow-hidden">
                  <div className="flex justify-between items-center mb-4 pb-1">
                     <h4 className="font-bold text-stripe-dark flex items-center gap-2 tracking-tight">
                        <History size={18} className="text-stripe-blue" /> Transaction History
                     </h4>
                     <button 
                       onClick={() => navigate('/transactions')} 
                       className="text-[10px] font-black text-stripe-blue uppercase tracking-widest hover:underline flex items-center gap-1"
                     >
                        View All <ChevronRight size={10} strokeWidth={3} />
                     </button>
                  </div>
                  <div className="space-y-4">
                     {repayments.length > 0 ? (
                        repayments.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 6).map(r => (
                           <div key={r.id} className="p-4 border border-stripe-bg bg-white/50 rounded-2xl flex items-center justify-between hover:border-stripe-border hover:bg-white transition-all shadow-sm shadow-stripe-blue/5">
                              <div className="flex items-center gap-4">
                               <div className="w-10 h-10 bg-stripe-bg rounded-xl flex items-center justify-center border border-stripe-border shadow-sm">
                                  <ArrowUpRight size={18} className="text-stripe-blue" />
                               </div>
                               <div>
                                  <p className="text-sm font-black text-stripe-dark leading-none">LKR {Math.round(r.amount).toLocaleString()}</p>
                                  <p className="text-[10px] text-stripe-slate uppercase font-bold tracking-wider mt-1.5">{r.option} Payment</p>
                               </div>
                            </div>
                            <div className="text-right flex flex-col items-end gap-1.5">
                               <StatusBadge status={r.status} />
                               <span className="text-[9px] font-bold text-stripe-light tracking-widest uppercase">
                                 {new Date(r.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                               </span>
                            </div>
                           </div>
                        ))
                     ) : (
                        <div className="py-12 text-center text-stripe-slate bg-stripe-bg/50 rounded-2xl border border-dashed border-stripe-border">
                           <History size={24} className="mx-auto mb-2 opacity-20" />
                           <p className="text-sm font-medium">No recent transactions to display</p>
                        </div>
                     )}
                  </div>
               </div>
            </div>
          </div>
        )}

        {/* Repayment Modal */}
        {showRepay && (
          <div className="s-modal-overlay">
            <div className="s-modal">
              <div className="s-modal-header">
                <h3 className="text-lg font-bold text-stripe-dark">Make a Payment</h3>
                <button onClick={() => setShowRepay(false)} className="btn-icon">✕</button>
              </div>
              <form onSubmit={handleRepaySubmit} className="s-modal-body space-y-6">
                 <div className="grid grid-cols-2 gap-4">
                    <button 
                      type="button" 
                      onClick={() => setRepayOption('installment')}
                      className={`p-4 rounded-xl border transition-all text-left ${repayOption === 'installment' ? 'border-stripe-dark bg-stripe-bg' : 'border-stripe-border hover:border-gray-300'}`}
                    >
                       <p className="text-sm font-semibold text-stripe-dark">Installment</p>
                       <p className="text-xs text-stripe-slate mt-1">Pay monthly due</p>
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setRepayOption('full')}
                      className={`p-4 rounded-xl border transition-all text-left ${repayOption === 'full' ? 'border-stripe-dark bg-stripe-bg' : 'border-stripe-border hover:border-gray-300'}`}
                    >
                       <p className="text-sm font-semibold text-stripe-dark">Full Balance</p>
                       <p className="text-xs text-stripe-slate mt-1">Settle entire loan</p>
                    </button>
                 </div>

                 <div className="p-4 bg-stripe-bg rounded-xl border border-stripe-border">
                    <div className="flex justify-between items-center text-stripe-dark mb-2">
                       <span className="text-sm font-medium">Amount to Pay</span>
                       <span className="text-lg font-bold">LKR {Number(loans.find(l => l.status === 'approved')?.[repayOption === 'installment' ? 'installments' : 'remainingBalance']?.[0]?.amount || 0).toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-stripe-slate">Verification will be processed within 1-2 business days.</p>
                 </div>

                 <button 
                    disabled={loading}
                    className="btn-primary w-full"
                 >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : 'Confirm Payment'}
                 </button>
              </form>
            </div>
          </div>
        )}

        {/* Application Modal */}
        {showApply && (
          <div className="s-modal-overlay">
            <div className="bg-white w-full max-w-3xl h-[90vh] md:h-auto md:max-h-[85vh] rounded-t-2xl md:rounded-2xl shadow-xl flex flex-col">
               <div className="p-6 border-b border-stripe-border flex justify-between items-center bg-white">
                  <div>
                     <h2 className="text-xl font-bold text-stripe-dark">Loan Application</h2>
                     <p className="text-sm text-stripe-slate mt-1">Step {currentStep} of 7</p>
                  </div>
                  <button onClick={() => setShowApply(false)} className="btn-icon">✕</button>
               </div>

               <form onSubmit={handleApply} className="flex-1 overflow-y-auto p-6 md:p-8 bg-white">
                 
                 {currentStep === 1 && (
                   <div className="text-center py-8 space-y-6">
                      <div className="w-20 h-20 bg-stripe-bg rounded-full flex items-center justify-center mx-auto border border-stripe-border">
                         <FileText size={32} className="text-stripe-dark" />
                      </div>
                      <div>
                         <h3 className="text-2xl font-bold text-stripe-dark">New Application</h3>
                         <p className="text-stripe-slate mt-2 max-w-md mx-auto">Follow the steps to submit your loan application and documents for review.</p>
                      </div>
                      <button type="button" onClick={() => setCurrentStep(2)} className="btn-primary mt-4">
                         Start Application <ChevronRight size={16} />
                      </button>
                   </div>
                 )}

                 {currentStep === 2 && (
                   <div className="space-y-6 animate-in fade-in">
                      <h4 className="text-lg font-bold text-stripe-dark">Loan Details</h4>
                      
                      <div className="space-y-4">
                         <div>
                            <label className="s-label">Loan Amount (LKR)</label>
                            <input 
                              type="number" 
                              className="s-input text-lg" 
                              placeholder={`Max: ${maxAllowed}`}
                              value={formData.loan.amount} 
                              onChange={(e) => updateFormData('loan', 'amount', e.target.value)} 
                              required 
                            />
                         </div>

                         <div>
                            <label className="s-label">Duration (Months)</label>
                            <div className="flex flex-wrap gap-3">
                              {[1, 2, 3, 6, 12, 18, 24].filter(m => !config?.maxDuration || m <= config.maxDuration).map(m => (
                                <button 
                                  key={m} type="button" 
                                  onClick={() => updateFormData('loan', 'duration', m.toString())}
                                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${formData.loan.duration === m.toString() ? 'border-stripe-dark bg-stripe-dark text-white' : 'border-stripe-border text-stripe-slate hover:bg-stripe-bg'}`}
                                >
                                  {m} Months
                                </button>
                              ))}
                            </div>
                         </div>

                         <div className="p-4 bg-stripe-bg rounded-xl border border-stripe-border mt-4">
                             <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-stripe-dark">Estimated Monthly</span>
                                 <span className="text-lg font-bold text-stripe-dark">LKR {Math.round((Number(formData.loan.amount) + calculateInterest(formData.loan.amount, formData.loan) + calculateServiceFee(formData.loan.amount)) / (Number(formData.loan.duration) || 1)).toLocaleString()}</span>
                             </div>
                         </div>
                      </div>
                   </div>
                 )}

                 {currentStep === 3 && (
                   <div className="space-y-6 animate-in fade-in">
                      <h4 className="text-lg font-bold text-stripe-dark">Identity Documents</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                         <label className={`block p-6 border border-dashed rounded-xl cursor-pointer hover:bg-stripe-bg ${personalFiles.nicFront ? 'border-stripe-dark bg-gray-50' : 'border-stripe-border'}`}>
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setPersonalFiles({...personalFiles, nicFront: e.target.files[0]})} />
                            <div className="text-center">
                               <Camera size={24} className="mx-auto mb-2 text-stripe-slate" />
                               <span className="text-sm font-medium text-stripe-dark block">NIC Front</span>
                               <span className="text-xs text-stripe-slate">{personalFiles.nicFront?.name || 'Click to upload'}</span>
                            </div>
                         </label>
                         
                         <label className={`block p-6 border border-dashed rounded-xl cursor-pointer hover:bg-stripe-bg ${personalFiles.nicBack ? 'border-stripe-dark bg-gray-50' : 'border-stripe-border'}`}>
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setPersonalFiles({...personalFiles, nicBack: e.target.files[0]})} />
                            <div className="text-center">
                               <Camera size={24} className="mx-auto mb-2 text-stripe-slate" />
                               <span className="text-sm font-medium text-stripe-dark block">NIC Back</span>
                               <span className="text-xs text-stripe-slate">{personalFiles.nicBack?.name || 'Click to upload'}</span>
                            </div>
                         </label>

                         <label className={`block p-6 border border-dashed rounded-xl cursor-pointer hover:bg-stripe-bg md:col-span-2 ${personalFiles.selfie ? 'border-stripe-dark bg-gray-50' : 'border-stripe-border'}`}>
                            <input type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => setPersonalFiles({...personalFiles, selfie: e.target.files[0]})} />
                            <div className="text-center">
                               <User size={24} className="mx-auto mb-2 text-stripe-slate" />
                               <span className="text-sm font-medium text-stripe-dark block">Selfie Photo</span>
                               <span className="text-xs text-stripe-slate">{personalFiles.selfie?.name || 'Click to upload'}</span>
                            </div>
                         </label>
                      </div>
                   </div>
                 )}

                  {currentStep === 4 && (
                   <div className="space-y-6 animate-in fade-in">
                      <h4 className="text-lg font-bold text-stripe-dark">Personal Information</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="md:col-span-2">
                             <label className="s-label">Full Name</label>
                             <input type="text" className="s-input" value={formData.personal.fullName} onChange={(e) => updateFormData('personal', 'fullName', e.target.value)} required />
                          </div>
                          <div>
                             <label className="s-label">NIC Number</label>
                             <input type="text" className="s-input uppercase" value={formData.personal.nicNumber} onChange={handleNICChange} required />
                          </div>
                          <div>
                              <label className="s-label">Mobile Number</label>
                              <div className="flex gap-2">
                                <input 
                                  type="tel" 
                                  className={`s-input flex-1 ${isPhoneVerified ? 'bg-green-50 border-green-200' : ''}`}
                                  value={formData.personal.mobileNumber} 
                                  onChange={(e) => updateFormData('personal', 'mobileNumber', sanitizePhone(e.target.value))} 
                                  disabled={isPhoneVerified}
                                  required 
                                />
                                {!isPhoneVerified && !showOtpInput && (
                                  <button 
                                    type="button" 
                                    onClick={handleSendUserOTP}
                                    disabled={isVerifyingPhone}
                                    className="px-4 py-2 bg-stripe-dark text-white rounded-lg text-xs font-bold whitespace-nowrap disabled:opacity-50"
                                  >
                                    {isVerifyingPhone ? '...' : 'Verify'}
                                  </button>
                                )}
                                {isPhoneVerified && (
                                  <div className="flex items-center gap-1.5 px-3 bg-green-100 text-green-700 rounded-lg text-[10px] font-bold border border-green-200">
                                    <CheckCircle2 size={12} /> VERIFIED
                                  </div>
                                )}
                              </div>
                              {showOtpInput && !isPhoneVerified && (
                                <div className="mt-2 flex gap-2 animate-in slide-in-from-top-2">
                                  <input 
                                    type="text" 
                                    placeholder="Enter OTP"
                                    className="s-input flex-1"
                                    maxLength={6}
                                    value={userOtp}
                                    onChange={(e) => setUserOtp(e.target.value)}
                                  />
                                  <button 
                                    type="button" 
                                    onClick={handleVerifyUserOTP}
                                    className="px-4 py-2 bg-stripe-blue text-white rounded-lg text-xs font-bold"
                                  >
                                    Confirm
                                  </button>
                                </div>
                              )}
                           </div>
                          <div>
                             <label className="s-label">Date of Birth</label>
                             <input type="date" className="s-input bg-gray-50 cursor-not-allowed" value={formData.personal.dob} disabled required />
                          </div>
                          <div>
                             <label className="s-label">Gender</label>
                             <select className="s-select bg-gray-50 cursor-not-allowed" value={formData.personal.gender} disabled required>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                             </select>
                          </div>
                          <div className="md:col-span-2">
                             <label className="s-label">Current Address</label>
                             <textarea className="s-textarea" rows="2" value={formData.personal.currentAddress} onChange={(e) => updateFormData('personal', 'currentAddress', e.target.value)} required />
                          </div>
                          <div>
                             <label className="s-label">Marital Status</label>
                             <select className="s-select" value={formData.personal.maritalStatus} onChange={(e) => updateFormData('personal', 'maritalStatus', e.target.value)}>
                                <option value="Single">Single</option>
                                <option value="Married">Married</option>
                                <option value="Divorced">Divorced</option>
                             </select>
                          </div>
                          <div>
                             <label className="s-label">Dependents</label>
                             <input type="number" className="s-input" value={formData.financial.dependents} onChange={(e) => updateFormData('financial', 'dependents', e.target.value)} />
                          </div>
                      </div>
                   </div>
                 )}

                 {currentStep === 5 && (
                   <div className="space-y-6 animate-in fade-in">
                      <h4 className="text-lg font-bold text-stripe-dark">Employment & Income</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                             <label className="s-label">Job Title</label>
                             <input type="text" className="s-input" value={formData.employment.jobTitle} onChange={(e) => updateFormData('employment', 'jobTitle', e.target.value)} required />
                          </div>
                          <div>
                             <label className="s-label">Company / Location</label>
                             <input type="text" className="s-input" value={formData.employment.workLocation} onChange={(e) => updateFormData('employment', 'workLocation', e.target.value)} required />
                          </div>
                          <div>
                             <label className="s-label">Monthly Income (LKR)</label>
                             <input type="number" className="s-input" value={formData.employment.monthlyIncome} onChange={(e) => updateFormData('employment', 'monthlyIncome', e.target.value)} required />
                          </div>
                          <div>
                             <label className="s-label">Existing monthly debt (LKR)</label>
                             <input
                               type="number"
                               min="0"
                               className="s-input"
                               placeholder="0 if none — other loans’ EMIs"
                               value={formData.financial.monthlyDebt}
                               onChange={(e) => updateFormData('financial', 'monthlyDebt', e.target.value)}
                             />
                          </div>
                          <div>
                             <label className="s-label">Bank Name (For Deposit)</label>
                             <select className="s-select" value={formData.loan.bankName} onChange={(e) => updateFormData('loan', 'bankName', e.target.value)} required>
                                <option value="" disabled>Select a Bank</option>
                                <option value="Commercial Bank">Commercial Bank</option>
                                <option value="Bank of Ceylon">Bank of Ceylon (BOC)</option>
                                <option value="Hatton National Bank">Hatton National Bank (HNB)</option>
                                <option value="Sampath Bank">Sampath Bank</option>
                                <option value="People's Bank">People's Bank</option>
                                <option value="National Development Bank">National Development Bank (NDB)</option>
                                <option value="Nations Trust Bank">Nations Trust Bank (NTB)</option>
                                <option value="Seylan Bank">Seylan Bank</option>
                                <option value="DFCC Bank">DFCC Bank</option>
                                <option value="Pan Asia Bank">Pan Asia Bank</option>
                                <option value="Union Bank">Union Bank</option>
                                <option value="Amana Bank">Amana Bank</option>
                                <option value="Sanasa Development Bank">Sanasa Development Bank (SDB)</option>
                                <option value="Cargills Bank">Cargills Bank</option>
                                <option value="Standard Chartered Bank">Standard Chartered Bank</option>
                                <option value="HSBC">HSBC</option>
                             </select>
                          </div>
                          <div>
                             <label className="s-label">Account Number</label>
                             <input type="text" className="s-input" value={formData.loan.accountNumber} onChange={(e) => updateFormData('loan', 'accountNumber', e.target.value)} required />
                          </div>
                          <div>
                             <label className="s-label">Account Holder Name</label>
                             <input type="text" className="s-input" value={formData.loan.accountHolder} onChange={(e) => updateFormData('loan', 'accountHolder', e.target.value)} required />
                          </div>
                      </div>

                      <div className="mt-4">
                         <label className="s-label">Bank Statements (Last 3 Months)</label>
                         <label className={`block p-6 border border-dashed rounded-xl cursor-pointer hover:bg-stripe-bg ${personalFiles.statements ? 'border-stripe-dark bg-gray-50' : 'border-stripe-border'}`}>
                            <input 
                               type="file" 
                               multiple
                               accept=".pdf,image/*"
                               className="hidden" 
                               onChange={(e) => {
                                  const files = Array.from(e.target.files);
                                  const pdfs = files.filter(f => f.type === 'application/pdf');
                                  const images = files.filter(f => f.type.startsWith('image/'));
                                  if (pdfs.length > 3) return alert('Maximum 3 PDFs allowed');
                                  if (images.length > 10) return alert('Maximum 10 images allowed');
                                  if (pdfs.length === 0 && images.length === 0) return alert('Please upload PDF or Image files only');
                                  setPersonalFiles({...personalFiles, statements: files});
                               }} 
                               required={!personalFiles.statements || personalFiles.statements.length === 0} 
                            />
                            <div className="text-center">
                               <Upload size={24} className="mx-auto mb-2 text-stripe-slate" />
                               <span className="text-sm font-medium text-stripe-dark block">Upload PDFs (Max 3) or Images (Max 10)</span>
                               <span className="text-xs text-stripe-slate">
                                  {personalFiles.statements?.length > 0 ? `${personalFiles.statements.length} files selected` : 'Required'}
                               </span>
                            </div>
                         </label>
                      </div>
                   </div>
                 )}

                  {currentStep === 6 && (
                    <div className="space-y-6 animate-in fade-in">
                       <h4 className="text-lg font-bold text-stripe-dark">Guarantor Details</h4>
                       <p className="text-sm text-stripe-slate">Add a guarantor for your application.</p>

                       <div className="space-y-4">
                          {formData.guarantors.map((guarantor, index) => (
                             <div key={index} className="p-5 border border-stripe-border rounded-xl space-y-4 bg-gray-50">
                                <div>
                                   <label className="s-label">Full Name</label>
                                   <input type="text" className="s-input" value={guarantor.name} onChange={(e) => {
                                      const newGuarantors = [...formData.guarantors];
                                      newGuarantors[index].name = e.target.value;
                                      setFormData({ ...formData, guarantors: newGuarantors });
                                   }} required />
                                </div>
                                <div>
                                   <label className="s-label">NIC Number</label>
                                   <input 
                                     type="text" 
                                     className="s-input uppercase" 
                                     value={guarantor.nic} 
                                     onChange={(e) => {
                                       const val = sanitizeNIC(e.target.value);
                                       const newGuarantors = [...formData.guarantors];
                                       newGuarantors[index].nic = val;
                                       setFormData({ ...formData, guarantors: newGuarantors });
                                     }} 
                                     required 
                                   />
                                </div>
                                <div>
                                   <label className="s-label">Mobile Number</label>
                                   <input 
                                     type="tel" 
                                     className="s-input" 
                                     value={guarantor.phone} 
                                     onChange={(e) => {
                                       const val = sanitizePhone(e.target.value);
                                       const newGuarantors = [...formData.guarantors];
                                       newGuarantors[index].phone = val;
                                       setFormData({ ...formData, guarantors: newGuarantors });
                                     }} 
                                     required 
                                   />
                                </div>
                             </div>
                          ))}
                       </div>
                    </div>
                  )}

                  {currentStep === 7 && (() => {
                     const totalInterestF = calculateInterest(formData.loan.amount, formData.loan);
                     const totalServiceFeeF = calculateServiceFee(formData.loan.amount);
                     const totalPayableF = Number(formData.loan.amount) + totalInterestF + totalServiceFeeF;
                     const dur = Number(formData.loan.duration) || 1;
                     const instAmount = Math.ceil(totalPayableF / dur);
                     const nextDue = new Date();
                     nextDue.setDate(nextDue.getDate() + 30);

                     return (
                        <div className="space-y-6 animate-in fade-in">
                           <h4 className="text-lg font-bold text-stripe-dark">Review Application</h4>
                           
                           <div className="p-5 bg-stripe-bg rounded-xl border border-stripe-border space-y-4">
                              <div className="flex justify-between">
                                 <span className="text-sm text-stripe-slate">Principal Amount</span>
                                 <span className="text-sm font-bold text-stripe-dark">LKR {Number(formData.loan.amount).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                 <span className="text-sm text-stripe-slate">Monthly Installment</span>
                                 <span className="text-sm font-bold text-stripe-blue">LKR {instAmount.toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                 <span className="text-sm text-stripe-slate">Duration (Period)</span>
                                 <span className="text-sm font-bold text-stripe-dark">{formData.loan.duration} Months</span>
                              </div>
                              <div className="flex justify-between">
                                 <span className="text-sm text-stripe-slate">First Due Date</span>
                                 <span className="text-sm font-bold text-stripe-dark">{nextDue.toLocaleDateString()}</span>
                              </div>
                              <div className="flex justify-between pt-3 border-t border-stripe-dark/10">
                                 <span className="text-sm text-stripe-slate">Total Payable</span>
                                 <span className="text-sm font-bold text-stripe-dark">LKR {Math.ceil(totalPayableF).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                 <span className="text-sm text-stripe-slate">Guarantor Name</span>
                                 <span className="text-sm font-bold text-stripe-dark">{formData.guarantors[0].name}</span>
                              </div>
                           </div>
                           <div className="flex items-start gap-3 mt-6">
                              <input 
                                type="checkbox" 
                                id="agree-terms"
                                className="mt-1 w-4 h-4 rounded border-stripe-border text-stripe-blue focus:ring-stripe-blue"
                                checked={agreedToTerms}
                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                              />
                              <label htmlFor="agree-terms" className="text-sm text-stripe-slate">
                                I have read and agree to the <button type="button" onClick={() => setShowAgreement(true)} className="text-stripe-blue font-medium hover:underline">Loan Agreement</button>.
                              </label>
                           </div>
                        </div>
                     );
                  })()}

              <div className="mt-8 flex gap-3">
                {currentStep > 1 && currentStep <= 7 && (
                  <button type="button" onClick={prevStep} className="btn-secondary">
                    Back
                  </button>
                )}
                
                {currentStep > 1 && currentStep < 7 && (
                  <button type="submit" className="btn-primary flex-1">
                    Continue
                  </button>
                )}

                {currentStep === 7 && (
                  <button type="submit" disabled={loading || !agreedToTerms} className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all">
                    {loading ? <Loader2 className="animate-spin" size={16} /> : 'Submit Application'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
      </main>
      
      {showAgreement && (() => {
        const totalInterestF = calculateInterest(formData.loan.amount, formData.loan);
        const totalServiceFeeF = calculateServiceFee(formData.loan.amount);
        const totalPayableF = Number(formData.loan.amount) + totalInterestF + totalServiceFeeF;
        const dur = Number(formData.loan.duration) || 1;
        const instAmount = Math.round(totalPayableF / dur);
        const nextDue = new Date();
        nextDue.setDate(nextDue.getDate() + 30);

        return (
          <div className="s-modal-overlay" style={{ zIndex: 100 }}>
             <div className="s-modal max-w-2xl max-h-[90vh] flex flex-col">
                <div className="s-modal-header shrink-0 border-b border-stripe-border">
                   <h3 className="text-lg font-bold text-stripe-dark">Loan Agreement</h3>
                   <button onClick={() => setShowAgreement(false)} className="btn-icon">✕</button>
                </div>
                <div className="s-modal-body p-6 overflow-y-auto space-y-4 text-sm text-stripe-slate text-left pb-12" style={{ fontFamily: "'Poppins', sans-serif" }}>
                   
                   <div className="text-center pb-4 border-b border-stripe-border">
                      <h2 className="text-base font-bold text-stripe-dark uppercase">Finova (Pvt) Ltd</h2>
                      <p className="text-xs">Reg. No. [COMPANY REG NO] | No. 72nd Floors, World Trade Center, Colombo 01, Sri Lanka</p>
                      <h3 className="text-sm font-bold text-stripe-dark uppercase mt-4">Personal Loan Agreement</h3>
                   </div>

                   <div className="text-[11px] leading-relaxed space-y-4 text-justify">
                      <p>This Term Loan Agreement is entered into between FINOVA (PVT) LTD, a company duly incorporated in Sri Lanka (Company Registration No. [COMPANY REG NO]) and having its registered office at No. 72nd Floors, World Trade Center, Colombo 01, Sri Lanka (hereinafter called and referred to as "the Company") and the BORROWER as identified in the Schedule hereto, subject to the terms and conditions of the Term Personal Loan Facility set out hereinbelow.</p>
                      
                      <p>WHEREAS the Company carries on business and in the ordinary course of its business lends monies and/or grants financial facilities to its customers;</p>
                      
                      <p>WHEREAS the said BORROWER has applied for a financial facility for personal purposes from the Company and the said Company agreed to grant the said financial facility subject to the terms and conditions as are more fully set out hereunder;</p>
                      
                      <p>The Company and Borrower shall individually be referred to as "Party" and collectively referred to as "Parties".</p>
                      
                      <div className="space-y-2 mt-6">
                         <h4 className="font-bold text-stripe-dark text-[12px]">1. Disbursement of Funds</h4>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">1.1.</span> <span>The Company shall grant a Financial Facility as more fully defined herein under the Schedule hereto and disbursed to the Borrower's savings account maintained with the Company or such other account that may be instructed by the Borrower.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">1.2.</span> <span>Notwithstanding any arrangement or writing to the contrary between the Parties, the Borrower hereby expressly agrees that the aforesaid amount together with all other charges as described in clause 2 (PAYMENTS) shall be repayable to the Company and all arrangements or indulgences or permitted to Borrower to repay by installments shall be subject to the principal obligation of repayment.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">1.3.</span> <span>The Borrower also agrees that any statement extract in writing, system generated or other document showing the monies owing hereunder by the Borrower to the Company and made out of the Company books or document and signed and certified by any authorized officer or generated by a Company system shall be deemed sufficient at Law, and conclusive proof against the Borrower, of the contents thereof without any other documents or vouchers to support the same.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2">
                         <h4 className="font-bold text-stripe-dark text-[12px]">2. Payments</h4>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">2.1.</span> <span>The Borrower shall pay the charges that shall be debited to the Borrower's Loan in connection with servicing of this Facility by the Company and payments made at any Branch and/or payment channel provided by the Company shall be deemed to have been made to the principal business place of the Company.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">2.2.</span> <span>Any delay and/or default of settling of the Monthly Installment payment due and owing from the Borrower shall be subject to default payment terms and conditions of this Agreement.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">2.3.</span> <span>The obligation to settle the Monthly Installment in terms of the payment terms shall at all times remain with the Borrower.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2">
                         <h4 className="font-bold text-stripe-dark text-[12px]">3. Terms and Conditions</h4>
                         <p className="pl-4 italic">Whereas the Borrower hereby agrees to abide by the following:</p>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.1.</span> <span>The Borrower agrees that the Company shall retain the right to place on hold the funds lying in the Borrower's savings account with the Company until this Facility is settled in full.</span></p>
                            <div className="space-y-2">
                               <p className="flex gap-2"><span className="shrink-0 font-medium">3.2.</span> <span>The BORROWER shall pay the following and any other charges that will be debited to BORROWER's loan account in connection with serving of this loan by the Company:</span></p>
                               <div className="pl-8 space-y-1 text-stripe-slate">
                                  <p className="flex gap-2"><span className="shrink-0">3.2.1.</span> <span>Charges on the normal and penalty interest.</span></p>
                                  <p className="flex gap-2"><span className="shrink-0">3.2.2.</span> <span>Postage and other handling charges if applicable.</span></p>
                                  <p className="flex gap-2"><span className="shrink-0">3.2.3.</span> <span>Inspection, valuation and any other incidental charges if applicable.</span></p>
                                  <p className="flex gap-2"><span className="shrink-0">3.2.4.</span> <span>Any Statutory payment by way of taxes or otherwise imposed by the Government or any other statutory body.</span></p>
                                  <p className="flex gap-2"><span className="shrink-0">3.2.5.</span> <span>Legal Fees.</span></p>
                                  <p className="flex gap-2"><span className="shrink-0">3.2.6.</span> <span>All other expenses incurred by the Company for the recovery of the monies owing from the Borrower to the Company.</span></p>
                               </div>
                            </div>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.3.</span> <span>Any delay and/or default of settling of the Monthly Installment payment due and owing from the Borrower shall be subject to default payment terms and conditions of this Agreement.</span></p>
                            <div className="space-y-2">
                               <p className="flex gap-2"><span className="shrink-0 font-medium">3.4.</span> <span>The Borrower shall immediately inform the Company in the event of the occurrence of the following events and shall if required by the Company settle in full the outstanding Loan amount which shall be payable by the Borrower to the Company:</span></p>
                               <div className="pl-8 space-y-1 text-stripe-slate">
                                  <p className="flex gap-2"><span className="shrink-0">3.4.1.</span> <span>Any material change is expected or experienced in his/her financial condition.</span></p>
                                  <p className="flex gap-2"><span className="shrink-0">3.4.2.</span> <span>Particulars of any litigation which may tend to affect the his/her financial capacity and in which the Borrower may be involved, of a termination or resignation or in a similar event of his/her employment.</span></p>
                               </div>
                            </div>
                            <div className="space-y-2">
                               <p className="flex gap-2"><span className="shrink-0 font-medium">3.5.</span> <span>The Company is hereby irrevocably authorized by the Borrower without notice to the Borrower:</span></p>
                               <div className="pl-8 space-y-1 text-stripe-slate">
                                  <p className="flex gap-2"><span className="shrink-0">3.5.1.</span> <span>to combine the account of this Facility with all or any of the Borrower's other accounts or deposits (term or demand); and</span></p>
                                  <p className="flex gap-2"><span className="shrink-0">3.5.2.</span> <span>to transfer and appropriate all monies lying to the Borrower's credit, including to the extent of closing any account in the Borrower's name, and the Borrower hereby undertakes to indemnify the Company against all claims or demands consequent on such action of the Borrower.</span></p>
                               </div>
                            </div>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.6.</span> <span>The production in any court of Law or before any Tribunal or body or statutory officer of any statement, extract, writing or other documents showing monies owing by the Borrower as a result of the Company granting this Facility and made out of the books or documents of the Company and signed and certified by the Manager or Accountant shall be deemed to be conclusive proof of the amounts of the Borrower's liability to the Company without any further documents or vouchers being produced by the Company.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.7.</span> <span>The monies due by the Borrower to the Company in respect of this Facility and interest thereon shall be recoverable from the Borrower notwithstanding the Prescription Ordinance (cap 68) and the Borrower shall not raise the Prescription Ordinance (cap 68) as a bar to the Company suing the Borrower for the recovery of the said monies.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.8.</span> <span>All representations and statements made by the Borrower or his/her agents are hereby warranted true and correct and intended to be acted upon by the Company and shall form the basis of the contract resulting from recovery of the said monies.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.9.</span> <span>The Borrower agrees that in the event of default by the Borrower, the Company may appoint a third-party collector for the recovery of outstanding amounts and the Borrower agrees to pay all costs of such collection.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.10.</span> <span>The Borrower agrees that the Company at its sole discretion may change any interest rates, service fees, and other charges applicable under this Loan Agreement from time to time. Provided however, the Company shall give Seven (07) days' notice to the Borrower prior to effecting such change.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.11.</span> <span>This Agreement shall be prima facie evidence of the amount owing by the Borrower to the Company.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.12.</span> <span>The Borrower shall not be entitled to a rebate in the event of an early settlement of the Facility.</span></p>
                            <div className="space-y-2">
                               <p className="flex gap-2"><span className="shrink-0 font-medium">3.13.</span> <span>The Company shall have the right to set-off/net off on the deposits of any kind and nature (including fixed deposits) held/balances lying in any accounts of the Borrower and on any monies, securities, bonds and all other assets, documents, deeds and properties held by/under the control of the Company/their trustees or agents to the extent of all outstanding dues. In addition to the above, the Borrower authorizes the Company:</span></p>
                               <div className="pl-8 space-y-1 text-stripe-slate">
                                  <p className="flex gap-2"><span className="shrink-0">3.13.1.</span> <span>to combine or consolidate at any time all or any of the accounts and liabilities of the Borrower with or to any branch of the Company; and</span></p>
                                  <p className="flex gap-2"><span className="shrink-0">3.13.2.</span> <span>to sell or dispose of any of the Borrower's securities or properties held by the Company by way of public or private sale or assignment or in any other manner whatsoever without having to institute any judicial proceeding and retain/appropriate from the proceeds the total amounts outstanding including costs and expenses in connection with such sale/disposal/transfer/assignment.</span></p>
                               </div>
                            </div>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.14.</span> <span>The Borrower shall not transfer ownership and/or vary his/her profile details of the registered mobile number and/or until the Facility is settled in full.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.15.</span> <span>In the event of any unsettled outstanding on the Facility, the Company reserves the right to instruct any Group entity to withhold any payment of commission, fees or other amounts payable to the Borrower until the outstanding payments are settled in full.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.16.</span> <span>The Company reserves the right to share its database/specific customer information with any one of its subsidiaries and/or holding companies in order to provide an integrated service to the customer and/or for the purpose of recovering any outstanding and/or with any Government authority with any criminal investigation or for the purpose of any criminal proceedings.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.17.</span> <span>The Borrower consents to the Company accessing the Borrower's National Identity Card (NIC) details maintained with the Department of Registration of Persons of Sri Lanka (DRP) to assess the authenticity of the information provided, including name, address, date of birth, place of birth, gender, date issued, photograph, and scanned copies of the identity card.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">3.18.</span> <span>The Borrower specifically consents to the Company accessing the Borrower's credit information available in the Credit Information Bureau (CRIB) for the purpose of assessing his/her credit management and to report to CRIB details of the Facility granted by the Company on a monthly basis.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2">
                         <h4 className="font-bold text-stripe-dark text-[12px]">4. Security for the Facility</h4>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">4.1.</span> <span>The security for the said Facility shall be a Personal Guarantor as set out in the Loan Application to this Agreement and as confirmed by the Company to the Customer via the Offer Letter.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">4.2.</span> <span>Prior to any disbursement by the Company under the Facility, the Borrower shall procure and furnish to the Company, in the form prescribed by the Company ("Guarantee and Indemnity"), an irrevocable and unconditional guarantee from the "Guarantor" for the due repayment/payment of the Facility including all interest thereon and other monies payable by the Borrower in respect of the Facility. The Borrower shall not pay any guarantee commission to the Guarantor. The said Guarantee and Indemnity shall be applicable throughout the validity of this Loan Agreement.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">4.3.</span> <span>The Company shall also have a lien over the savings account whereas the Company shall place a hold on the savings account 07 days (or such other number of days as decided at the sole discretion of the Company) prior to the loan due date.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2">
                         <h4 className="font-bold text-stripe-dark text-[12px]">5. Material Breach</h4>
                         <p className="pl-4 italic">This Agreement shall be deemed to have been breached in the event that:</p>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">5.1.</span> <span>The Borrower fails to pay the amount on the due date and fails to pay the total amount due with interest on demand.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">5.2.</span> <span>The Borrower fails and/or neglects to adhere by any terms or condition of this Agreement.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2">
                         <h4 className="font-bold text-stripe-dark text-[12px]">6. Consequence of Breach / Default</h4>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">6.1.</span> <span>In the event of the occurrence of any act of material breach the Company may at its discretion terminate this Agreement provided that such event of default remains not remedied 07 days after such default being notified by the Company to the Borrower. Termination of this Agreement shall be without prejudice to the respective rights and obligations accrued under this Agreement prior to such termination.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">6.2.</span> <span>Without prejudice to the Company's right to exercise any remedies available under law with respect to recovering the monies owed by the Borrower, subsequent to termination the Company reserves the right to take all necessary legal action to recover the outstanding amounts.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">6.3.</span> <span>It being agreed by the Borrower that pursuant to such termination, the total Loan Amount together with other fees, charges and dues of the Borrower under this Agreement including the cost of recovering the Facility shall fall due and shall be payable by the Borrower to the Company forthwith on demand.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">6.4.</span> <span>The Company may terminate this Agreement at any time in the event of a default other than a material breach, provided that such event of default remains not remedied 7 days after such default being notified by the Company to the Borrower.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">6.5.</span> <span>TERMS AND CONDITIONS (clause 3), PAYMENT (clause 2), EXCLUSIVE JURISDICTION (clause 9) shall survive the termination of the Agreement.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2">
                         <h4 className="font-bold text-stripe-dark text-[12px]">7. Waiver and Variation</h4>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">7.1.</span> <span>Any delay, failure or omission by either party in enforcing, exercising or pursuing any right, power or privilege, claim or remedy set out hereunder or any breach by either party of its obligations hereunder shall not be deemed to be construed as a waiver thereof or operate so as to bar the enforcement or exercise thereof or of any other such right, power, privilege, claim or remedy in any other instance at any time or times thereafter.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2">
                         <h4 className="font-bold text-stripe-dark text-[12px]">8. Severability / Illegality</h4>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">8.1.</span> <span>If any term or provision in this Agreement shall be held to be illegal or unenforceable, in whole or in part, under any enactment or rule of law, such term or provision or part thereof shall to that extent be deemed not to form part of this Agreement but the enforceability of the remainder of this Agreement shall not be affected.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2">
                         <h4 className="font-bold text-stripe-dark text-[12px]">9. Exclusive Jurisdiction</h4>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">9.1.</span> <span>This Agreement and the security afforded hereunder shall be governed and construed in all respects in accordance with the Law of Sri Lanka and the Court of jurisdiction shall be Colombo.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2">
                         <h4 className="font-bold text-stripe-dark text-[12px]">10. Notices</h4>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">10.1.</span> <span>All demands under this Agreement may be made by the Company by any authorized person or firm for the time being acting as Lawyer or Lawyers for the Company by a letter addressed to the "Borrower" and sent by post or delivered to the address herein before stated and Notice of Demand so given or made shall be deemed to be given or made or received on the day it was so delivered or the day following that on which it is posted as the case may be.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">10.2.</span> <span>All reminders and notices shall be sent by short message services (SMS), emails or any other acceptable communication method notified to the customer from time to time to the registered mobile telephone number or Electronic mail given by the Borrower and the Borrower hereby states that the said messages being electronic messages shall be deemed to be legally binding reminders.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">10.3.</span> <span>It is further agreed that all payments shall be receipted/acknowledged whether the payments are treated as payments/damages/statutory payments/service charges.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2">
                         <h4 className="font-bold text-stripe-dark text-[12px]">11. Miscellaneous</h4>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">11.1.</span> <span>The Company reserves the right to amend or vary the terms and conditions of this Agreement from time to time whereas the Company shall notify the Borrower of any changes to these terms and conditions prior to effecting the same.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">11.2.</span> <span>The Company may transfer or assign its rights and obligations under this Agreement and such transfer or assignment shall be effective upon notification of the same to the Borrower.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">11.3.</span> <span>In the event of any inconsistency between these terms and conditions and any Sinhala or Tamil translation of these terms and conditions, the English terms and conditions shall prevail.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2">
                         <h4 className="font-bold text-stripe-dark text-[12px]">12. Early Settlement</h4>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">12.1.</span> <span>Early Settlement shall be subject to the discretion of the Company.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">12.2.</span> <span>An early settlement fee of 1.5% of the outstanding loan amount will be charged, subject to a minimum fee of Rs. 50 and a maximum of Rs. 1,000.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2">
                         <h4 className="font-bold text-stripe-dark text-[12px]">13. Recovery Procedure</h4>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">13.1.</span> <span>The Company shall terminate the Agreement giving the Borrower 07 days' notice in the event of a default.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">13.2.</span> <span>The Company shall proceed with the relevant recovery steps in terms of the Agreement and the applicable laws of Sri Lanka.</span></p>
                         </div>
                      </div>

                      <div className="space-y-2 pt-2 pb-4">
                         <h4 className="font-bold text-stripe-dark text-[12px]">14. Definitions</h4>
                         <div className="pl-4 space-y-2">
                            <p className="flex gap-2"><span className="shrink-0 font-medium">14.1.</span> <span>Borrower – Means the individual on whose request the Company has granted the Term Loan and who has agreed to these terms and conditions by placing his/her digital signature in terms of the Electronic Transactions Act No. 19 of 2006 as amended.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">14.2.</span> <span>Facility – Means the loan facility more fully defined under the Schedule hereto granted to the Borrower under this Term Loan scheme by Finova Capital Pvt Ltd.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">14.3.</span> <span>Agreement – Means this Loan Agreement.</span></p>
                            <p className="flex gap-2"><span className="shrink-0 font-medium">14.4.</span> <span>Loan Application – Means the loan application containing the Borrower's details and the facility details required by the Borrower as duly completed by the Borrower and submitted to the Company in writing or submitted electronically as the case may be.</span></p>
                         </div>
                      </div>
                   </div>

                   <div className="mt-8 pt-6 border-t-2 border-stripe-dark/20 text-xs bg-gray-50 p-4 rounded-xl">
                      <h4 className="font-bold text-sm text-stripe-dark uppercase mb-4 text-center">Schedule — Loan Details</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3">
                         <div className="text-stripe-slate">Loan Reference Number</div><div className="font-semibold text-stripe-dark">[LOAN REF NUMBER GENERATED UPON APPROVAL]</div>
                         <div className="text-stripe-slate">Borrower Full Name</div><div className="font-semibold text-stripe-dark">{formData.personal.fullName || '[BORROWER FULL NAME]'}</div>
                         <div className="text-stripe-slate">NIC Number</div><div className="font-semibold text-stripe-dark">{formData.personal.nicNumber || '[NIC NUMBER]'}</div>
                         <div className="text-stripe-slate">Contact Number</div><div className="font-semibold text-stripe-dark">{formData.personal.mobileNumber || '[CONTACT NUMBER]'}</div>
                         <div className="text-stripe-slate">Address</div><div className="font-semibold text-stripe-dark">{formData.personal.currentAddress || '[BORROWER ADDRESS]'}</div>
                         
                         <div className="text-stripe-slate mt-2 text-[10px] sm:col-span-2 border-b border-stripe-border pb-1">Facility Details</div>
                         
                         <div className="text-stripe-slate">Loan Amount (Principal)</div><div className="font-semibold text-stripe-dark">LKR {Number(formData.loan.amount).toLocaleString()}</div>
                         <div className="text-stripe-slate">Interest Rate</div><div className="font-semibold text-stripe-dark">{formData.loan.interestRate}% {formData.loan.rateType}</div>
                         <div className="text-stripe-slate">Loan Tenure</div><div className="font-semibold text-stripe-dark">{formData.loan.duration} Months</div>
                          <div className="text-stripe-slate">Monthly Installment</div><div className="font-semibold text-stripe-dark">LKR {instAmount.toLocaleString()}</div>
                         <div className="text-stripe-slate">First Installment Due Date</div><div className="font-semibold text-stripe-dark">{nextDue.toLocaleDateString()}</div>
                         <div className="text-stripe-slate">Disbursement Date</div><div className="font-semibold text-stripe-dark">{new Date().toLocaleDateString()} (Estimated)</div>
                         <div className="text-stripe-slate">Disbursement Account</div><div className="font-semibold text-stripe-dark">{formData.loan.accountNumber || '[ACCOUNT NUMBER]'}</div>
                         
                         <div className="text-stripe-slate mt-2 text-[10px] sm:col-span-2 border-b border-stripe-border pb-1">Guarantor Details</div>
                         
                         <div className="text-stripe-slate">Guarantor Name</div><div className="font-semibold text-stripe-dark">{formData.guarantors[0].name || '[GUARANTOR FULL NAME]'}</div>
                         <div className="text-stripe-slate">Guarantor NIC</div><div className="font-semibold text-stripe-dark">{formData.guarantors[0].nic || '[GUARANTOR NIC]'}</div>
                      </div>
                      <p className="mt-6 text-stripe-slate italic text-center">Signed and executed by the Parties in terms of the Electronic Transactions Act No. 19 of 2006 as amended.</p>
                      

                      
                      <div className="text-center mt-12 pt-6 border-t border-stripe-border text-[9px]">
                         Finova Capital Pvt Ltd | 72nd Floors, World Trade Center, Colombo 01 | Tel: +94 11 234 5678 | Email: info@finova.lk | www.finova.lk<br />
                         This document is legally binding upon execution by both parties in accordance with the laws of Sri Lanka.
                      </div>

                      <div className="mt-8 pt-8 border-t border-stripe-border">
                         <SignaturePad label="BORROWER OFFICIAL SIGNATURE" onSave={setSignature} />
                         <p className="text-[9px] text-stripe-slate mt-2 italic">By signing this pad, you confirm that you have read and accepted all 14 clauses of this Term Loan Agreement and that all information provided is accurate and true.</p>
                      </div>
                   </div>

                   <div className="pt-6 mt-6 sticky bottom-0 bg-white/95 backdrop-blur py-4 border-t border-stripe-border px-6">
                      <button 
                        onClick={() => { setAgreedToTerms(true); setShowAgreement(false); }} 
                        disabled={!signature}
                        className="btn-primary w-full py-4 rounded-xl shadow-lg disabled:opacity-50 transition-all font-bold tracking-wider"
                      >
                        {signature ? 'ACCEPT & SIGN AGREEMENT' : 'PLEASE SIGN TO CONTINUE'}
                      </button>
                   </div>
                </div>
             </div>
          </div>
        );
      })()}

      <BottomNav />
    </div>
  );
}
